import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Loader2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import { getArticleRevision } from '../../../services/api';

interface VersionPreviewDialogProps {
  helpCenterId: string;
  itemId: string;
  revisionId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Read-only render of a single past version (its content as readers would have seen it).
export const VersionPreviewDialog: React.FC<VersionPreviewDialogProps> = ({
  helpCenterId,
  itemId,
  revisionId,
  open,
  onOpenChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [html, setHtml] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !revisionId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getArticleRevision(helpCenterId, itemId, revisionId);
        if (cancelled) return;
        if (res.ok && res.revision) {
          setTitle(res.revision.title || '');
          setDescription(res.revision.description || '');
          setHtml(res.revision.content_html || '');
        } else {
          setError(res.error || 'Failed to load version');
        }
      } catch {
        if (!cancelled) setError('Failed to load version');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, revisionId, helpCenterId, itemId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Version preview</DialogTitle>
        </DialogHeader>

        <div className="max-h-[65vh] overflow-y-auto rounded-md border p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-destructive">{error}</div>
          ) : (
            <div className="tiptap-editor">
              <h1>{title}</h1>
              {description && <p className="text-muted-foreground">{description}</p>}
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VersionPreviewDialog;
